import axios from "axios";
import {
  BASE_API,
  getScheduleData, //de_duplicate_schedules
} from "./entities";
import { v4 as uuidv4 } from "uuid";
import { isEmpty } from "lodash";
import {
  filterScheduleByStartDate,
  isUpdateRequired,
  parseBigSchedules,
  fetchRecentByCreatedOrUpdate,
} from "../util/filterSchedules";
import { setScheduleId, update_schedule_ids_in_final_form } from "../actions";

/**
 * Function to get the schedues from big schedule api.
 * @param {string} carrierCode - carrier scac codes.
 * @param {string} originCode - origin port code.
 * @param {string} destinationCode - destination port code.
 * @param {Redux.Store} getstate - Whole redux store state
 * @returns {Array} - Returns schedule data from api.
 */
export const getBigScheduleDataFromAPI = async (
  carrierCode,
  originCode,
  destinationCode,
  getstate
) => {
  const isUserPresent =
    getstate()?.route?.USER_DETAILS?.webuser?.userid != null;

  let jwt_token;

  if (isUserPresent) {
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
    //console.log("web user: ",jwt_token)
  } else {
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN;
    //console.log("public user: ",jwt_token)
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  const actionId = isUserPresent
    ? "4e435fac095746e58e3ad1c4068d0233"
    : "159252b19d25499ca0ede5ee97bb372b";

  const input = {
    carrier_code: carrierCode,
    destination_code: destinationCode,
    origin_code: originCode,
  };

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
      action_id: actionId,
      input,
    },
  };

  return axios.post(BASE_API, query, { headers }).then((result) => {
    if (result?.data?.data?.action?.results) {
      const responseData = result?.data?.data?.action?.results;
      if (responseData && !isEmpty(responseData)) {
        return [200, responseData];
      }
    }
    return [403, []];
  });
};

/**
 * Function to store the schedues from big schedule api.
 * @param {Object} rateObject - rate information for which schedule need to be stored.
 * @param {Array} data - parsed schedule data.
 * @param {Redux.Store} getstate - Whole redux store state
 * @returns {Array} - Returns stored schedules data from bb.
 */
export const store_schedules = async (rateObject, getstate, data) => {
  // console.log(
  //   "store_schedules is executing.....",
  //   data,
  //   "rateObject ",
  //   rateObject,
  // );
  const webuser = getstate().route?.USER_DETAILS?.webuser;

  let jwt_token;

  if (webuser && webuser.userid) {
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  } else {
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN;
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  const actionId =
    webuser && webuser.userid
      ? "6d13553068bd44b8bd154ca26904aac4"
      : "c0dca5af8c42488eb003999849d2ca81";

  const input = {
    ScheduleInfo: data,
    rateObject: rateObject,
  };

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
      action_id: actionId,
      input,
    },
  };

  return axios.post(BASE_API, query, { headers }).then((result) => {
    // console.log("result: ",result)
    if (result?.data?.data?.action?.results) {
      const responseData = result?.data?.data?.action?.results || [];
      if (responseData) {
        return [200, responseData];
      }
    }
    return [403, []];
  });
};

/**
 * Function to store new port.
 * @param {string} country - country name.
 * @param {string} portName - new port name.
 * @param {string} unlocode - unlocode of new port.
 * @param {Redux.Store} getState - Whole redux store state
 * @returns {Array} - Returns the new port Id.
 */
export const sendNewPortRequest = async (
  country,
  portName,
  unlocode,
  getstate
) => {
  //console.log("---sendNewPortRequest--- ",country, portName, unlocode);
  const isUserPresent =
    getstate()?.route?.USER_DETAILS?.webuser?.userid != null;

  let jwt_token;
  if (isUserPresent) {
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  } else {
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN;
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + jwt_token + "",
  };

  const actionId = isUserPresent
    ? "50fff064eb5a4b96b02e5fcd8449ab42"
    : "3e9ec7a32d25468db70d81710896c5ee";

  const input = {
    country: country,
    portName: portName,
    unlocode: unlocode,
  };

  const query = {
    query: "mutation { action(id: $action_id input: $input) }",
    variables: {
      action_id: actionId,
      input,
    },
  };

  return axios.post(BASE_API, query, { headers }).then((result) => {
    if (
      !result?.data?.data?.errors &&
      result?.data?.data?.action &&
      result?.data?.data?.action.results
    ) {
      return [true, result.data.data.action.results];
    }
    return [false];
  });
};

/**
 * Function to get carrier codes comma saparated list.
 * @param {Array} rates_from_graphql - The list of rates with carrier data.
 * @returns {string} - Returns carrier codes.
 */
export const getCarrierCodeList = (rates_from_graphql, isClient = false) => {
  let carrier_codes = [
    ...new Set(rates_from_graphql.map((rate) => 
      isClient 
          ? rate?.chargetypes?.carrier?.scac_code 
          : rate?.carrier?.scac_code
  )),
  ].join(",");
  if (carrier_codes !== "" && !carrier_codes.includes("CMDU")) {
    carrier_codes = `${carrier_codes},CMDU`;
  } else if (carrier_codes === "") {
    carrier_codes = "CMDU";
  }
  return carrier_codes;
};

/**
 * Function to get schedues from Big schedule API and parse the data.
 * @param {Array} carrier_codes - The list of carrier scac codes.
 * @param {Redux.Store} getstate - Whole redux store state
 * @param {Function} dispatch - It does dispatch a redux action to update the redux state
 * @returns {Array} - Returns parsed schedule data.
 */
export const get_parsed_schedules = async (
  carrier_codes,
  getstate,
  dispatch
) => {
  const origin = getstate()?.route?.queryParams?.from?.portCode;
  const destination = getstate()?.route?.queryParams?.to?.portCode;

  const response = await getBigScheduleDataFromAPI(
    carrier_codes,
    origin,
    destination,
    getstate
  );
  // console.log("API response:", response);

  let scheculeAPIData = [];
  if (response[0] === 200 && response?.[1] && !isEmpty(response[1])) {
    scheculeAPIData = response[1];
  }

  // console.log("Schedule data from API:", scheculeAPIData);

  // Parse the schedule data and update rates
  const parsedData = await parseBigSchedules(
    scheculeAPIData,
    getstate,
    dispatch
  );
  // console.log("-- ---- ---- ----parsed data-- ---- ----", parsedData);
  return parsedData;
};

//actual function
/**
 * Function to add the schedues to rates.
 * @param {Array} rates_from_graphql - The list of rates.
 * @param {Redux.Store} getstate - Whole redux store state
 * @param {Function} dispatch - It does dispatch a redux action to update the redux state
 * @returns {Array} - Returns final rates with schedule data.
 */
export const add_fcl_schedule_rates = async (
  rates_from_graphql,
  getstate,
  dispatch,
  isClient = false
) => {
  // Initializing variables
  let rates_needing_update = [];
  let parsedData = [];
  let scheduleInfo = [];
  let rates_with_valid_schedules_arr = [];
  const isUserPresent =
    getstate()?.route?.USER_DETAILS?.webuser?.userid != null;

  // Fetch data from the DB and determine which rates need updates
  const scheduleResults = await Promise.all(
    rates_from_graphql.map(async (rates_object) => {
      const rateObjectId = isClient ? (rates_object?.chargetypes?.id) : (rates_object.id)
      const result = await getScheduleData(
        rateObjectId,
        isUserPresent,
        getstate
      );

      if (
        !result ||
        result[0] !== 200 ||
        !result[1] ||
        result[1].length === 0
      ) {
        return { rates_object, needsUpdate: true };
      }

      scheduleInfo = result[1];
      if (scheduleInfo) {
        const recentSchedules = fetchRecentByCreatedOrUpdate(scheduleInfo);
        const recentfilteredSchedules =
          filterScheduleByStartDate(recentSchedules);
        let de_duplicated_schedules = de_duplicate_schedules(
          recentfilteredSchedules,
          true
        );
        const filteredSchedules = de_duplicate_schedules_further(
          de_duplicated_schedules,
          true
        );

        if (isUpdateRequired(filteredSchedules)) {
          return { rates_object, needsUpdate: true };
        } else {
          rates_object.schedules = filteredSchedules;
          const rates_with_valid_schedules = add_schedules_to_rates(
            filteredSchedules,
            rates_object
          );
          rates_with_valid_schedules_arr.push(...rates_with_valid_schedules);
          return { rates_object, needsUpdate: false };
        }
      }

      return { rates_object, needsUpdate: false };
    })
  );

  // Separate rates needing update and rates with valid schedules
  rates_needing_update = scheduleResults
    .filter(({ needsUpdate }) => needsUpdate)
    .map(({ rates_object }) => rates_object);

  // If any rates need an update, fetch schedules from the API
  let rates_with_schedules = [];
  if (rates_needing_update.length > 0) {
    const carrier_codes = getCarrierCodeList(rates_needing_update, isClient);
    parsedData = await get_parsed_schedules(carrier_codes, getstate, dispatch);

    // Only for display purposes
    rates_with_schedules = await update_fcl_schedules_display(
      rates_needing_update,
      parsedData,
      getstate,
      dispatch,
      isClient
    );
  }

  // Merge both updated schedules and schedules fetched from the DB
  const final_rates = [
    ...rates_with_valid_schedules_arr,
    ...rates_with_schedules,
  ];

  //filter out the rates which has same origin and destination and carrier which has same carrier 
  const fileterdFinalRates = filterRatesBySchedules(final_rates, isClient);
  // console.log("fileterdFinalRates: ",fileterdFinalRates)
  // Return the final rates with schedules
  return fileterdFinalRates
};

/**
 * Function to flattens all the weekly schedules into a single array.
 * @param {object} weeklyScheduleMap - The list of rates.
 * @returns {Array} - Returns the flattened array.
 */
const flattenAndFilterSchedules = (weeklyScheduleMap) => {
  if (!weeklyScheduleMap || typeof weeklyScheduleMap !== "object") return [];

  return Object.values(weeklyScheduleMap).reduce((acc, weekArr) => {
    if (Array.isArray(weekArr) && weekArr.length > 0) {
      acc.push(...weekArr);
    }
    return acc;
  }, []);
};

/**
 * Function to update the rates with the parsed schedules obtained from the api.
 * @param {Array} rates_from_graphql - The list of rates.
 * @param {Array} parsedSchedules - schedules.
 * @param {Redux.Store} getstate - Whole redux store state
 * @param {Function} dispatch - It does dispatch a redux action to update the redux state
 * @returns {Array} - Returns the rates with the schedules.
 */
export const update_fcl_schedules_display = async (
  rates_from_graphql,
  parsedSchedules,
  getstate,
  dispatch,
  isClient = false
) => {
  const rates_with_schedules = [];

  // Promise.all(
  //   rates_from_graphql?.map((rate) =>
  //     storeUpdateSchedules(rate, parsedSchedules, getstate, dispatch)
  //   )
  // )
  //   .then(() => {
  //     console.log("All schedules are stored");
  //   })
  //   .catch((error) => {
  //     console.error("Error:", error);
  //   });

  for (const rates_object of rates_from_graphql) {
    // Trigger async storage and ID dispatch, don't await it to keep UI responsive

    storeUpdateSchedules(rates_object, parsedSchedules, getstate, dispatch, isClient)
      .then(() => {
       console.log("All schedules are stored");
    })
    .catch((error) => {
      console.log("error while storing schedule")
      console.error("Error:", error);
    });

    // === UI display flow ===
    const filteredScheduleMap = filter_from_parsed_schedules(
      parsedSchedules,
      rates_object,
      isClient
    );
    const filteredSchedules = flattenAndFilterSchedules(filteredScheduleMap);
    // console.log("----before de_duplication...", filteredSchedules);
    const uniqueParsedSchedules = de_duplicate_schedules(
      filteredSchedules,
      false
    );
    // console.log(
    //   "----uniqueParsedSchedules after de_duplication-----",
    //   uniqueParsedSchedules
    // );
    const de_duplicated_schedules = de_duplicate_schedules_further(
      uniqueParsedSchedules,
      false
    );
    // console.log(
    //   "---further de_duplicated schedules-----",
    //   de_duplicated_schedules
    // );
    const hashedSchedules = generate_local_schedule_keys(
      de_duplicated_schedules,
      rates_object,
      false
    );
    const uiRateObjects = add_schedules_to_rates(hashedSchedules, rates_object);

    rates_with_schedules.push(...uiRateObjects);
  }

  return rates_with_schedules;
};

/**
 * Function to store/update the schedules in the bb and assign the stored schedule id to corresponding rates with schedule.
 * @param {object} rateObject - rate object.
 * @param {Array} parsedSchedules - parsed schedules.
 * @param {Redux.Store} getstate - Whole redux store state
 * @param {Function} dispatch - It does dispatch a redux action to update the redux state
 * @returns
 */
export const storeUpdateSchedules = async (
  rateObject,
  parsedSchedules,
  getstate,
  dispatch,
  isClient
) => {
  // console.log("storeUpdateSchedules is executing.....", rateObject)
  const isUserPresent =
    getstate()?.route?.USER_DETAILS?.webuser?.userid != null;
  try {
    const storedScheduleMap = await refresh_fcl_schedules(
      rateObject,
      parsedSchedules,
      getstate,
      isClient
    );
    const storedSchedules = flattenAndFilterSchedules(storedScheduleMap);
    // console.log("---- ... before de_duplication from db...", storedSchedules);
    const uniqueStoredSchedules = de_duplicate_schedules(storedSchedules, true);
    // console.log(
    //   "--- ... uniqueStoredSchedules from db ... -----",
    //   uniqueStoredSchedules
    // );
    const de_duplicated_schedules = de_duplicate_schedules_further(
      uniqueStoredSchedules,
      true
    );
    // console.log(
    //   "---further de_duplicated schedules-----",
    //   de_duplicated_schedules
    // );
    const hashedSchedules = generate_local_schedule_keys(
      de_duplicated_schedules,
      rateObject,
      true
    );
    const storedRateObjects = add_schedules_to_rates(
      hashedSchedules,
      rateObject
    );

    if (!storedRateObjects?.length) return;
    const rateType = isClient ? (rateObject?.chargetypes?.rate_type) : (rateObject?.rate_type)
    const type = (rateType === "FCL" ? "api-fcl" : "api-lcl");

    storedRateObjects.forEach((rate) => {
      const rateId = isClient ? (rate?.chargetypes?.id) : (rate?.id)
      dispatch(
        setScheduleId(rateId, rate?.schedules, rate?.local_schedule_key, type)
      );
      if (isUserPresent) {
        dispatch(
          update_schedule_ids_in_final_form(
            rateId,
            rate?.local_schedule_key,
            rate?.schedules
          )
        );
      }
    });
  } catch (error) {
    console.error("Failed to store FCL schedules:", error);
  }
};

/**
 * Function to generate the hash for given input.
 * @param {object} inputObj - input object.
 * @returns {string} - returns hash
 */
const generateHash = (inputObj) => {
  // Sort the object keys to ensure deterministic string representation
  const ordered = {};
  Object.keys(inputObj)
    .sort()
    .forEach((key) => {
      ordered[key] = inputObj[key];
    });

  const str = JSON.stringify(ordered);

  // djb2 hash algorithm (fast and good for short strings)
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }

  return (hash >>> 0).toString(36); // Base36 makes it compact and readable
};

/**
 * Function to generate a unique hash key to identify each schedule for mapping purposes.
 * @param {Array} schedule_arr - schedules.
 * @param {object} rates_object - rate object.
 * @param {boolean} isDbVersion - The `dbVersion` parameter determines the structure of the schedule data:
 * - Pass `false` when schedules are not yet stored (i.e., using the non-DB structure).
 * - Pass `true` when schedules are being processed with the database structure (which has different field names).
 * @returns {object} - returns the schedule object along with local_schedule_key(hash)
 */
const generate_local_schedule_keys = (
  schedule_arr,
  rates_object,
  isDbVersion = false
) => {
  return schedule_arr.map((schedule) => {
    // Determine the transshipmentIds based on the schedule data
    const transshipmentIds = Array.isArray(schedule.transshipments)
      ? schedule.transshipments
          .map((trans) =>
            isDbVersion
              ? trans?.port_of_transhipment?.id
              : trans.port_of_transshipment_id
          )
          .sort()
      : [];

    // Create the common hash input structure
    const hashInput = {
      rateId: rates_object?.id,
      origin: isDbVersion ? schedule?.origin?.id : schedule?.originPortId,
      destination: isDbVersion
        ? schedule?.destination?.id
        : schedule?.destinationPortId,
      discharge: isDbVersion ? schedule?.discharge?.id : schedule?.dischargeId,
      etd: isDbVersion ? schedule?.etd?.substring(0, 10) : schedule?.etd,
      etaPortOfDischarge: isDbVersion
        ? schedule?.eta_port_of_discharge?.substring(0, 10)
        : schedule?.etaPortOfDischarge,
      etaPortOfDestination: isDbVersion
        ? schedule?.eta_destination.substring(0, 10)
        : schedule?.etaPortOfDestination,
      vessel: isDbVersion ? schedule?.mother_vessel_name : schedule?.vesselName,
      voyage: schedule?.voyage,
      service: schedule?.service,
      transitTime: Number(
        isDbVersion ? schedule?.transit_time : schedule?.portTransitTime
      ),
      transshipmentIds,
    };

    // console.log("generate_local_schedule_keys -- hashInput: ", hashInput);
    const local_schedule_key = generateHash(hashInput);
    // console.log(
    //   "generate_local_schedule_keys -- local_schedule_key: ",
    //   local_schedule_key
    // );

    return {
      ...schedule,
      local_schedule_key,
    };
  });
};

/**
 * Function to append schedules to the rate object.
 * @param {Array} schedule_arr - schedule array.
 * @param {object} rates_object - given rate object.
 * @returns {Array} - returns rates with schedules.
 */
const add_schedules_to_rates = (schedule_arr, rates_object) => {
  // console.log("add_schedules_to_rates........",schedule_arr, rates_object)
  let rates_with_schedules = [];
  if (Array.isArray(schedule_arr) && schedule_arr.length > 0) {
    schedule_arr.forEach((schedule) => {
      let new_rate_object = { ...rates_object }; // Clone the rates object
      new_rate_object.schedules = schedule; // Assign the specific schedule to the new rate object
      new_rate_object.uuid = uuidv4();
      new_rate_object.local_schedule_key = schedule.local_schedule_key;
      rates_with_schedules.push(new_rate_object); // Push the new rate with schedule
    });
  } else {
    //console.log("No schedules available for rate:", rates_object.id);
    let new_rate_object = { ...rates_object }; // Clone the rates object
    new_rate_object.schedules = undefined; // No schedules available
    new_rate_object.uuid = uuidv4();
    new_rate_object.local_schedule_key = undefined;
    // Push only once if no matching schedules are found
    if (!rates_with_schedules.some((rate) => rate.id === new_rate_object.id)) {
      rates_with_schedules.push(new_rate_object); // Push the rate with no schedule if it doesn't already exist
    }
  }
  return rates_with_schedules;
};

/**
 * Function to filter parsed schedules by matching the origin, discharge, and destination ports with those in the existing rates.
 * @param {Array} parsedSchedules - parsed weekly schedule array.
 * @param {object} payloadSource - given rate object.
 * @returns {Array} - returns the filtered schedules.
 */
const filter_from_parsed_schedules = (parsedSchedules, payloadSource, clientRequest = false) => {
  let payload = {}
  if(clientRequest){
    payload = {
      originPort: payloadSource?.chargetypes?.location_from?.id,
      destinationPort: payloadSource?.chargetypes?.location_to?.id,
      dischargePort: payloadSource?.chargetypes?.via_port?.id,
      carrierCode: payloadSource?.chargetypes?.carrier?.scac_code,
    };
  }else{
    payload = {
      originPort: payloadSource?.location_from?.id,
      destinationPort: payloadSource?.location_to?.id,
      dischargePort: payloadSource?.via_port?.id,
      carrierCode: payloadSource?.carrier?.scac_code,
    };
  }

  // console.log("payload: ", payload, "parsedSchedules: ",parsedSchedules);
  const filteredSchedules = {};
  for (const week in parsedSchedules) {
    if (
      parsedSchedules.hasOwnProperty(week) &&
      week.startsWith(payload.carrierCode)
    ) {
      const schedules = parsedSchedules[week];
      const filtered = schedules.filter((schedule) => {
        // const isDischargePortValid =
        //   (payload.dischargePort !== payload.destinationPort)
        //     ? schedule.dischargeId === payload.dischargePort
        //     : true;

        const isDischargePortValid =
          schedule.dischargeId === payload.dischargePort ||
          (!schedule.dischargeId &&
            payload.dischargePort === payload.destinationPort);

        // console.log("schedule: ",schedule,"\nschedule.dischargeId: ",schedule.dischargeId,
        //   "\npayload.dischargePort: ",payload.dischargePort,
        // "\nisDischargePortValid: ",isDischargePortValid)

        return (
          schedule.originPortId === payload.originPort &&
          isDischargePortValid &&
          schedule.destinationPortId === payload.destinationPort
        );
      });
      // console.log("filtered: ",filtered)
      if (filtered.length > 0) filteredSchedules[week] = filtered;
    }
  }
// console.log("filteredSchedules: ",filteredSchedules)
  return filteredSchedules;
};

/**
 * Function to refresh/store the fcl weekly schedules.
 * @param {object} rates_object - given rate object.
 * @param {Array} parsedSchedules - parsed weekly schedule array.
 * @param {Redux.Store} getstate - Whole redux store state
 * @returns {Array} - returns the refreshed weekly schedules.
 */
export const refresh_fcl_schedules = async (
  rates_object,
  parsedSchedules,
  getstate,
  isClient
) => {
  // console.log("Refreshing FCL schedules for rate object:", rates_object);
  let payload = {}
  if(isClient){
    payload = {
      originPort: rates_object?.chargetypes?.location_from?.id,
      destinationPort: rates_object?.chargetypes?.location_to?.id,
      dischargePort: rates_object?.chargetypes?.via_port?.id,
      carrierID: rates_object?.chargetypes?.carrier?.id,
      carrierCode: rates_object?.chargetypes?.carrier?.scac_code,
      chargeId: rates_object?.chargetypes?.id,
      vesselId: rates_object?.chargetypes?.vessel_service?.id,
    };
  }else{
    payload = {
      originPort: rates_object?.location_from?.id,
      destinationPort: rates_object?.location_to?.id,
      dischargePort: rates_object?.via_port?.id,
      carrierID: rates_object?.carrier?.id,
      carrierCode: rates_object?.carrier?.scac_code,
      chargeId: rates_object?.id,
      vesselId: rates_object?.vessel_service?.id,
    };
  }

  // console.log("Payload for refreshing schedules:", payload);

  const filteredSchedules = filter_from_parsed_schedules(
    parsedSchedules,
    rates_object,
    isClient
  );
  // console.log("Filtered schedules data:", filteredSchedules);

  let weekly_schedules = {};

  if (filteredSchedules && Object.keys(filteredSchedules).length > 0) {
    try {
      const weekPromises = Object.keys(filteredSchedules).map(async (week) => {
        const weeklyData = filteredSchedules[week];
        if (weeklyData && weeklyData.length > 0) {
          const result = await store_schedules(payload, getstate, weeklyData);
          // console.log(`Result for week ${week}:`, result);
          if (
            result &&
            result[0] === 200 &&
            result[1] &&
            result[1].length > 0
          ) {
            weekly_schedules[week] = result[1];
          }
        }
      });
      // Wait for all weekly schedule data to be stored in parallel
      await Promise.all(weekPromises);
    } catch (err) {
      console.error("Error storing weekly schedules:", err);
    }
  }

  // console.log("Refreshed weekly schedules:", weekly_schedules);
  return await Promise.resolve(weekly_schedules);
};

// Normalize string values: removes "TBA" and dynamic UUIDs
const normalizeValue = (value) => {
  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const uuidWithDynamicSuffixRegex =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}-.*/;
    if (value.includes("TBA") || uuidWithDynamicSuffixRegex.test(value))
      return "";
    return value.trim();
  }

  return value;
};

// Extracts 'YYYY-MM-DD' from datetime
const getDatePart = (datetime) =>
  datetime ? new Date(datetime).toISOString().split("T")[0] : "";

// Generate base key used for deduplication
const buildVoyageKey = (voyage, useStoredSchedule, includeDates = false) => {
  const origin = useStoredSchedule ? voyage.origin?.id : voyage.originPortId;
  const discharge = useStoredSchedule
    ? voyage.discharge?.id
    : voyage.dischargeId;
  const destination = useStoredSchedule
    ? voyage.destination?.id
    : voyage.destinationPortId;

  const vesselName = useStoredSchedule
    ? voyage.mother_vessel_name
    : voyage.vesselName;
  const service = voyage.service;
  const voyageName = voyage.voyage;

  const dateETD = includeDates ? getDatePart(voyage.etd) : "";
  const dateETA = includeDates
    ? getDatePart(voyage.eta_destination || voyage.etaPortOfDestination)
    : "";

  return [
    normalizeValue(origin),
    normalizeValue(discharge),
    normalizeValue(destination),
    includeDates
      ? normalizeValue(voyage.transit_time || voyage.portTransitTime)
      : "",
    dateETD,
    dateETA,
    normalizeValue(vesselName),
    normalizeValue(service),
    normalizeValue(voyageName),
  ]
    .filter(Boolean)
    .join("-");
};

// Build transshipment key
const buildTransshipmentKey = (
  transshipments,
  useStoredSchedule,
  minimal = false
) => {
  if (!Array.isArray(transshipments) || transshipments.length === 0) return "";

  return transshipments
    .map((trans) => {
      if (minimal) {
        const id = useStoredSchedule
          ? trans.port_of_transhipment?.id
          : trans.port_of_transshipment_id;
        return normalizeValue(id);
      } else {
        const parts = useStoredSchedule
          ? [
              trans?.port_of_transhipment?.id,
              trans?.vessel,
              trans?.service,
              trans?.voyage,
            ]
          : [
              trans?.port_of_transshipment_id,
              trans?.vessel,
              trans?.route,
              trans?.voyage,
            ];
        return normalizeValue(parts.filter(Boolean).join("-"));
      }
    })
    .join(",");
};

/**
 * Function to remove duplicate schedules from a parsed weekly schedule array.
 * It generates a unique key based on various schedule fields (such as origin, destination, vessel name, voyage, service) and filters out duplicates.
 * Additionally, it normalizes certain values (e.g., treating "TBA" as an empty string) and allows the use of stored schedule data for comparison.
 *
 * @param {Array} schedules - The array of parsed weekly schedules to be de-duplicated.
 * @param {boolean} useStoredSchedule - Flag indicating whether to use stored schedule data for comparison (default is false).
 * @returns {Array} - The filtered array with duplicates removed, based on the generated unique keys.
 */
export const de_duplicate_schedules = (
  schedules,
  useStoredSchedule = false
) => {
  const seen = new Map();
  const filteredSchedules = [];

  schedules.forEach((voyage, index) => {
    const baseKey = buildVoyageKey(voyage, useStoredSchedule, false);
    const transKey = buildTransshipmentKey(
      voyage.transshipments,
      useStoredSchedule,
      false
    );
    const uniqueKey = `${baseKey}-${transKey}`;
    // console.log(uniqueKey);
    if (!useStoredSchedule) {
      seen.set(uniqueKey, index); // keep last occurrence
    } else if (!seen.has(uniqueKey)) {
      seen.set(uniqueKey, index);
      filteredSchedules.push(voyage); // keep first occurrence
    }
  });

  return useStoredSchedule
    ? filteredSchedules
    : Array.from(seen.values()).map((i) => schedules[i]);
};

/**
 * Function to further remove duplicate schedules from a parsed weekly schedule array.
 * It generates a unique key based on various schedule fields (such as origin, destination, vessel name, voyage, along with etd and eta_destination.) and filters out duplicates.
 * Additionally, it normalizes certain values (e.g., treating "TBA" as an empty string) and allows the use of stored schedule data for comparison.
 *
 * @param {Array} schedules - The array of parsed weekly schedules to be de-duplicated.
 * @param {boolean} useStoredSchedule - Flag indicating whether to use stored schedule data for comparison (default is false).
 * @returns {Array} - The filtered array with duplicates removed, based on the generated unique keys.
 */
export const de_duplicate_schedules_further = (
  schedules,
  useStoredSchedule = false
) => {
  const seen = new Map();
  const filteredSchedules = [];

  schedules.forEach((voyage, index) => {
    const baseKey = buildVoyageKey(voyage, useStoredSchedule, true); // includes dates
    const transKey = buildTransshipmentKey(
      voyage.transshipments,
      useStoredSchedule,
      true
    ); // only ID
    const uniqueKey = `${baseKey}-${transKey}`;
    // console.log(uniqueKey);
    if (!useStoredSchedule) {
      seen.set(uniqueKey, index); // keep last occurrence
    } else if (!seen.has(uniqueKey)) {
      seen.set(uniqueKey, index);
      filteredSchedules.push(voyage); // keep first occurrence
    }
  });

  return useStoredSchedule
    ? filteredSchedules
    : Array.from(seen.values()).map((i) => schedules[i]);
};

/**
 * Filters a list of rates by removing entries that lack schedules
 * when scheduled equivalents are available for the same origin, destination, and carrier.
 *
 * This function groups rates by a unique combination of origin, destination, and carrier.
 * Within each group, if both scheduled and unscheduled rates exist, it removes unscheduled
 * rates that duplicate the 40' price of a scheduled rate. Otherwise, all rates are retained.
 *
 * @param {Array} rates - The array of rate objects to be filtered.
 * @param {boolean} isClient - Flag indicating whether to use nested schedule data inside `chargetypes` (defaults to false).
 * @returns {Array} - A filtered array of rate objects with relevant schedule-based de-duplication applied.
 */
function filterRatesBySchedules(rates, isClient = false) {
  const grouped = {};

  // Step 1: Group by origin-destination-carrier combo
  rates.forEach(rate => {
    const key = isClient
      ? `${rate?.chargetypes?.location_from?.id}-${rate?.chargetypes?.location_to?.id}-${rate?.chargetypes?.carrier?.id}`
      : `${rate?.location_from?.id}-${rate?.location_to?.id}-${rate?.carrier?.id}`;
    
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(rate);
  });

  const filteredRates = [];

  // Step 2: Filter each group
  Object.values(grouped).forEach(group => {
    const withSchedules = group.filter(rate => rate.schedules && !isEmpty(rate.schedules));
    const withoutSchedules = group.filter(rate => !rate.schedules || isEmpty(rate.schedules));

    // console.log("withSchedules: ",withSchedules,
    //   "\nwithoutSchedules: ",withoutSchedules
    // )

    if (withSchedules.length > 0 && withoutSchedules.length > 0) {
      // Collect 40st rates from those that have schedules
      const scheduled40stRates = withSchedules.map(rate => rate?.["40price"]).filter(rate => rate != null);
      // console.log("scheduled40stRates: ",scheduled40stRates)
      // Filter out only those without schedules that match any 40st rate of a scheduled one
      const keepWithoutSchedules = withoutSchedules.filter(rate => {
        const rate40st = rate?.["40price"];
        return !scheduled40stRates.includes(rate40st); // Keep it only if its 40st rate is different
      });
      // console.log("keepWithoutSchedules: ",keepWithoutSchedules)
      // Combine kept "no-schedule" rates and all scheduled ones
      filteredRates.push(...withSchedules, ...keepWithoutSchedules);
    } else {
      // All have or don't have schedules — keep all
      filteredRates.push(...group);
    }
  });

  return filteredRates;
}


